import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import ChatHistoryRow from "./ChatHistoryRow";
import ChatHistoryEmptyState from "./ChatHistoryEmptyState";
import ChatHistoryLoadMore from "./ChatHistoryLoadMore";

export default function ChatHistoryPanel({
    isOpen,
    onClose,
    chats,
    selectedStepChatId,
    onSelect,
    onRefresh,
    isLoading,
    hasMore,
    onLoadMore,
    isLoadingMore,
}) {
    const { t } = useTranslation();
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [isOpen, onClose]);

    useEffect(() => {
        if (isOpen && onRefresh) onRefresh();
    }, [isOpen, onRefresh]);

    const handleOpenChat = useCallback(
        (chat) => {
            onSelect(chat.isActive ? null : chat.id);
            onClose();
        },
        [onSelect, onClose]
    );

    if (!isOpen) return null;

    return (
        <div className="absolute inset-x-0 top-12 bottom-0 z-50 flex flex-col bg-white animate-in fade-in duration-150">
            <div className="flex items-start justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
                <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">
                        {t("chatTwoColumnPage.pastConversationsTitle")}
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                        {t("chatTwoColumnPage.pastConversationsSubtitle", {
                            count: chats.length,
                        })}
                    </p>
                </div>
                <button
                    onClick={onClose}
                    className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex-shrink-0"
                    aria-label={t("common.close")}
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
            <div className="flex-1 overflow-y-auto">
                {chats.length === 0 ? (
                    <ChatHistoryEmptyState isLoading={isLoading} />
                ) : (
                    <>
                        <ul className="divide-y divide-gray-50">
                            {chats.map((chat) => (
                                <ChatHistoryRow
                                    key={chat.id}
                                    chat={chat}
                                    isCurrentSelection={
                                        (chat.isActive && !selectedStepChatId) ||
                                        chat.id === selectedStepChatId
                                    }
                                    isExpanded={expandedId === chat.id}
                                    onToggleExpand={() =>
                                        setExpandedId(
                                            expandedId === chat.id ? null : chat.id
                                        )
                                    }
                                    onOpen={() => handleOpenChat(chat)}
                                />
                            ))}
                        </ul>
                        {hasMore && (
                            <ChatHistoryLoadMore
                                onLoadMore={onLoadMore}
                                isLoadingMore={isLoadingMore}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
